<template>
  <div
    class="preview-item__wrap flex flex-col overflow-auto mt-4 max-h-[12.5rem]"
  >
    <div
      v-for="(attachment, index) in attachments"
      :key="attachment.id || attachment._clientId || index"
      class="preview-item flex items-center p-1 bg-slate-50 dark:bg-slate-800 rounded-md w-[15rem] mb-1"
      :class="{ 'is-uploading': attachment.uploading }"
      :aria-busy="attachment.uploading ? 'true' : 'false'"
    >
      <div class="thumb-wrap">
        <img
          v-if="isTypeImage(attachment.resource)"
          class="image-thumb"
          :class="{ 'is-uploading': attachment.uploading }"
          :src="attachment.thumb"
          alt=""
        />
        <span v-else class="attachment-icon" aria-hidden="true">📄</span>
        <span
          v-if="attachment.uploading"
          class="upload-spinner"
          aria-hidden="true"
        />
      </div>
      <div class="file-details">
        <span class="file-name" :title="fileName(attachment.resource)">
          {{ fileName(attachment.resource) }}
        </span>
        <span v-if="attachment.uploading" class="upload-status">
          {{ $t('CONVERSATION.REPLYBOX.UPLOADING') }}
          <template v-if="attachment.progress">
            {{ attachment.progress }}%
          </template>
        </span>
        <span
          v-if="attachment.uploading"
          class="upload-bar"
          :class="{ 'is-indeterminate': !attachment.progress }"
          role="progressbar"
          :aria-valuenow="attachment.progress || 0"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="$t('CONVERSATION.REPLYBOX.UPLOADING')"
        >
          <span class="upload-bar-fill" :style="progressStyle(attachment)" />
        </span>
        <span v-else class="file-size">
          {{ formatFileSize(attachment.resource) }}
        </span>
      </div>
      <div class="flex items-center justify-center">
        <woot-button
          v-if="canRemove(attachment)"
          type="button"
          class="remove--attachment clear secondary"
          :aria-label="
            attachment.uploading
              ? $t('CONVERSATION.REPLYBOX.CANCEL_UPLOAD')
              : undefined
          "
          icon="dismiss"
          @click="() => onRemoveAttachment(attachment, index)"
        />
      </div>
    </div>
  </div>
</template>
<script>
import { formatBytes } from 'shared/helpers/FileHelper';
export default {
  props: {
    attachments: {
      type: Array,
      default: () => [],
    },
    removeAttachment: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    onRemoveAttachment(attachment, index) {
      if (attachment && attachment.uploading) {
        this.$emit('cancel-upload', attachment.uploadId);
        return;
      }
      const clientId = attachment && attachment.clientId;
      this.removeAttachment(clientId || index);
    },
    canRemove(attachment) {
      // Uploads without their own id are part of a shared request and can't be
      // cancelled one by one.
      if (attachment.uploading) return !!attachment.uploadId;
      return !this.isTypeAudio(attachment.resource);
    },
    progressStyle(attachment) {
      if (!attachment || !attachment.progress) return null;
      return { width: `${attachment.progress}%` };
    },
    formatFileSize(file) {
      const size = file.byte_size || file.size;
      return formatBytes(size, 0);
    },
    isTypeImage(file) {
      const type = (file && (file.content_type || file.type)) || '';
      return type.includes('image');
    },
    isTypeAudio(file) {
      const type = (file && (file.content_type || file.type)) || '';
      return type.includes('audio');
    },
    fileName(file) {
      if (!file) return '';
      return file.filename || file.name || '';
    },
  },
};
</script>
<style lang="scss" scoped>
.image-thumb {
  @apply w-6 h-6 object-cover rounded-sm;

  &.is-uploading {
    @apply opacity-40;
  }
}

.thumb-wrap {
  @apply relative w-8 h-8 flex-shrink-0 flex items-center justify-center;
}

.attachment-icon {
  @apply text-lg leading-none;
}

.file-details {
  @apply flex flex-col flex-1 min-w-0 mx-2;
}

.file-name {
  @apply block overflow-hidden text-sm font-medium text-ellipsis whitespace-nowrap text-slate-800 dark:text-slate-100;
}

.file-size,
.upload-status {
  @apply block overflow-hidden text-xs text-slate-500 dark:text-slate-300 text-ellipsis whitespace-nowrap;
}

.upload-status {
  @apply text-woot-600 dark:text-woot-400;
}

.upload-bar {
  @apply relative mt-1 h-1 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden;
}

.upload-bar-fill {
  @apply block h-full bg-woot-500 rounded-full transition-all duration-150 ease-out;
}

.upload-bar.is-indeterminate .upload-bar-fill {
  width: 40%;
  animation: upload-indeterminate 1.1s ease-in-out infinite;
}

.upload-spinner {
  position: absolute;
  inset: 0;
  width: 1rem;
  height: 1rem;
  margin: auto;
  border-radius: 9999px;
  border: 2px solid rgba(31, 147, 255, 0.28);
  border-top-color: #1f93ff;
  animation: upload-spin 0.7s linear infinite;
}

.preview-item.is-uploading {
  @apply border border-woot-200 dark:border-woot-700;
  animation: upload-pulse 1.4s ease-in-out infinite;
}

.file-name-wrap,
.file-size-wrap {
  @apply flex items-center py-0 px-1;

  > .item {
    @apply m-0 overflow-hidden text-xs font-medium;
  }
}

.remove--attachment {
  @apply w-6 h-6 rounded-md text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800;
}

@keyframes upload-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes upload-indeterminate {
  0% {
    transform: translateX(-120%);
  }

  100% {
    transform: translateX(280%);
  }
}

@keyframes upload-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(31, 147, 255, 0.15);
  }

  50% {
    box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.12);
  }
}
</style>
